/**
 * AfterCare — Reusable accordion component.
 *
 * Vanilla TypeScript, no framework. Returns raw DOM elements
 * matching the existing dark-theme design system.
 *
 * Usage:
 *   const { element, contentEl } = createAccordion({ title: 'Diagnoses', icon: '🩺' });
 *   contentEl.appendChild(myRows);
 *   parent.appendChild(element);
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AccordionOptions {
  title: string;
  badgeText?: string;
  badgeColor?: string;
  defaultOpen?: boolean;
  icon?: string;
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function createAccordion(options: AccordionOptions): {
  element: HTMLElement;
  contentEl: HTMLElement;
  toggle: () => void;
  open: () => void;
  close: () => void;
} {
  // -- Section wrapper --
  const section = document.createElement('div');
  section.className = 'accordion-section';
  section.style.cssText = 'margin-bottom:8px;border-radius:12px;background:var(--color-bg-card,#fdfbf8);border:1px solid var(--color-border,#e2ddd6);overflow:hidden;box-shadow:0 1px 3px rgba(0,0,0,0.06);';

  // -- Header button --
  const header = document.createElement('button');
  header.className = 'accordion-header';
  header.setAttribute('aria-expanded', options.defaultOpen ? 'true' : 'false');
  header.style.cssText = 'width:100%;display:flex;align-items:center;justify-content:space-between;padding:16px 16px;background:none;border:none;color:var(--color-text,#2d3748);font-size:16px;font-weight:600;cursor:pointer;text-align:left;gap:8px;min-height:52px;';

  // Title group (icon + title + badge)
  const titleGroup = document.createElement('div');
  titleGroup.style.cssText = 'display:flex;align-items:center;gap:8px;flex:1;';

  if (options.icon) {
    const iconEl = document.createElement('span');
    iconEl.textContent = options.icon;
    iconEl.style.fontSize = '18px';
    titleGroup.appendChild(iconEl);
  }

  const titleEl = document.createElement('span');
  titleEl.textContent = options.title;
  titleGroup.appendChild(titleEl);

  if (options.badgeText) {
    const badge = document.createElement('span');
    badge.textContent = options.badgeText;
    badge.style.cssText = `font-size:12px;padding:3px 8px;border-radius:10px;background:${options.badgeColor || 'var(--color-primary,#3b82f6)'};color:white;font-weight:500;`;
    titleGroup.appendChild(badge);
  }

  // Chevron
  const chevron = document.createElement('span');
  chevron.textContent = '\u25B8'; // ▸
  chevron.style.cssText = 'transition:transform 0.2s;font-size:18px;color:var(--color-text-muted,#5a6578);';
  if (options.defaultOpen) chevron.style.transform = 'rotate(90deg)';

  header.appendChild(titleGroup);
  header.appendChild(chevron);

  // -- Content panel --
  const content = document.createElement('div');
  content.className = 'accordion-content';
  content.style.cssText = 'padding:0 16px;max-height:0;overflow:hidden;transition:max-height 0.3s ease,padding 0.3s ease;';
  if (options.defaultOpen) {
    content.style.maxHeight = 'none';
    content.style.paddingBottom = '16px';
  }

  // -- Toggle logic --
  let isOpen = !!options.defaultOpen;

  function toggle(): void {
    isOpen = !isOpen;
    header.setAttribute('aria-expanded', String(isOpen));
    if (isOpen) {
      content.style.maxHeight = content.scrollHeight + 100 + 'px';
      content.style.paddingBottom = '16px';
      chevron.style.transform = 'rotate(90deg)';
    } else {
      content.style.maxHeight = '0';
      content.style.paddingBottom = '0';
      chevron.style.transform = 'rotate(0deg)';
    }
  }

  function open(): void { if (!isOpen) toggle(); }
  function close(): void { if (isOpen) toggle(); }

  header.addEventListener('click', toggle);

  // -- Assemble --
  section.appendChild(header);
  section.appendChild(content);

  return { element: section, contentEl: content, toggle, open, close };
}
